import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useState } from 'react';
import { CatalogCarousel } from '@/components/catalog-carousel';
import {
  LayoutSize,
  useLayoutSizeValue,
  useSmallLayoutSizeValue,
} from '@/contexts/layout-context';
import {
  PromotionListOption,
  usePromotionListOptions,
} from '@/hooks/use-promotion-list-options';
import { StyleProp, StyleSheet, ViewStyle, ViewToken } from 'react-native';
import { useComponentFocus } from '@/hooks/use-component-focus';
import { useTrackPromotionImpressions } from '@/hooks/use-track-promotion-impressions';
import { useVolumePromotionsContext } from '@/contexts/volume-promotions-context';
import { VolumePromotionListItem } from './volume-promotion-list-item';
import { VolumePromotionListItemOrderableItem } from './volume-promotion-list-item-orderable-item';
import { VolumePromotionsSeeAllCard } from './volume-promotions-see-all-card';

const VOLUME_PROMOTION_SEE_ALL_UUID = 'VolumePromotionSeeAll';

const SEE_ALL_OPTION: PromotionListOption = {
  title: VOLUME_PROMOTION_SEE_ALL_UUID,
  value: VOLUME_PROMOTION_SEE_ALL_UUID,
};

const LAYOUT_SIZE_TO_COLUMNS_MAP = {
  [LayoutSize.SMALL]: 1,
  [LayoutSize.SMALL_TABLET]: 2,
  [LayoutSize.MEDIUM]: 3,
  [LayoutSize.LARGE]: 4,
};
const LIST_ITEM_MARGIN = 16;
const LIST_ITEM_MARGIN_LARGE = 8;

interface Props {
  contentContainerStyle?: StyleProp<ViewStyle>;
  largeHeaderStyle?: StyleProp<ViewStyle>;
  largeListStyle?: StyleProp<ViewStyle>;
  locationPrefix: string;
  maxLength?: number;
  onNavigateToDetails: (promotionUUID: string) => void;
  onSeeAllPress: () => void;
  titleOverride?: string;
  visibleUUIDs?: Set<string>;
}

const MIN_ITEM_WIDTH = 344;

export const VolumePromotionsCarousel: React.FC<Props> = ({
  contentContainerStyle,
  largeHeaderStyle,
  largeListStyle,
  locationPrefix,
  maxLength,
  onNavigateToDetails,
  onSeeAllPress,
  titleOverride,
  visibleUUIDs,
}) => {
  const promotions = usePromotionListOptions(visibleUUIDs);
  const { markPromotionsViewed } = useVolumePromotionsContext();
  const { onViewableItemsChanged } = useTrackPromotionImpressions({
    trackingLocation: locationPrefix,
  });

  const [listWidth, setListWidth] = useState(0);

  useComponentFocus({
    onFocus: useCallback(markPromotionsViewed, [markPromotionsViewed]),
  });

  const listItemMargin = useSmallLayoutSizeValue(
    LIST_ITEM_MARGIN,
    LIST_ITEM_MARGIN_LARGE,
  );

  const baseVisibleItemCount = useLayoutSizeValue(LAYOUT_SIZE_TO_COLUMNS_MAP);

  const { itemWidth, visibleItemCount } = useMemo(() => {
    // Calculate optimal column count: use the minimum of layout-based count and max possible columns
    const maxPossibleColumns = Math.floor(listWidth / MIN_ITEM_WIDTH);
    const optimalColumns = Math.min(
      baseVisibleItemCount,
      Math.max(1, maxPossibleColumns),
    );

    return {
      itemWidth: Math.round(listWidth / optimalColumns),
      visibleItemCount: optimalColumns,
    };
  }, [listWidth, baseVisibleItemCount]);

  const limitedPromotions = promotions.slice(0, maxLength ?? promotions.length);
  const showSeeAllCard = limitedPromotions.length > 1;
  const carouselData = useMemo(
    () =>
      showSeeAllCard
        ? [...limitedPromotions, SEE_ALL_OPTION]
        : limitedPromotions,
    [limitedPromotions, showSeeAllCard],
  );

  const handleViewableItemsChanged = useCallback(
    ({ viewableItems }: { viewableItems: ViewToken[] }) => {
      onViewableItemsChanged({
        viewableItems: viewableItems.filter(
          (vi) => vi.item.value !== VOLUME_PROMOTION_SEE_ALL_UUID,
        ),
      });
    },
    [onViewableItemsChanged],
  );

  return limitedPromotions.length ? (
    <CatalogCarousel
      containerStyle={styles.carouselContainer}
      contentContainerStyle={[
        styles.carouselContentContainer,
        contentContainerStyle,
      ]}
      data={carouselData}
      disableIntervalMomentum
      itemCount={carouselData.length}
      itemWidth={itemWidth}
      largeHeaderStyle={largeHeaderStyle}
      largeListStyle={largeListStyle}
      limit={carouselData.length}
      onLayout={(e) => {
        setListWidth(e.nativeEvent.layout.width);
      }}
      onSeeAllPress={onSeeAllPress}
      onViewableItemsChanged={handleViewableItemsChanged}
      pagingEnabled={false}
      renderItem={({ item }) => {
        if (item.value === VOLUME_PROMOTION_SEE_ALL_UUID) {
          return (
            <VolumePromotionsSeeAllCard
              count={promotions.length}
              onPress={onSeeAllPress}
              style={{
                paddingLeft: Math.max(0, 16 - listItemMargin),
                paddingRight: listItemMargin,
              }}
            />
          );
        }

        const { promotion } = item.metadata!;

        const sharedProps = {
          promotion,
          source: locationPrefix,
          style: {
            paddingHorizontal: listItemMargin,
            width: itemWidth,
          },
        };

        return promotion.promotedVariantPacks.length === 1 ? (
          <VolumePromotionListItemOrderableItem {...sharedProps} />
        ) : (
          <VolumePromotionListItem
            {...sharedProps}
            onNavigateToDetails={onNavigateToDetails}
          />
        );
      }}
      showNavigationDots
      snapToAlignment="start"
      snapToInterval={itemWidth}
      style={styles.carousel}
      testID="promotions-carousel"
      title={titleOverride ?? i18n.t('promotions')}
      visibleItemCount={visibleItemCount}
    />
  ) : null;
};

const styles = StyleSheet.create({
  carousel: {
    overflow: 'visible',
  },
  carouselContainer: {
    paddingBottom: 0,
  },
  carouselContentContainer: {
    paddingVertical: 4,
  },
});
